import type { ApiErrorBody } from "@/types/api";

/**
 * Fetch wrapper handling auth, errors, and silent token refresh.
 *
 * Token strategy, and why it looks like this:
 *
 * - The **access token lives in a module variable**, never in localStorage or
 *   sessionStorage. Web storage is readable by any script on the page, so a
 *   single XSS bug would hand an attacker a bearer token. A variable dies with
 *   the tab and is not reachable from an injected `<script>` after the fact.
 * - The **refresh token is an HttpOnly cookie** the browser attaches
 *   automatically. JavaScript cannot read it — including this file.
 * - A page reload therefore starts with no access token, and `bootstrapSession`
 *   trades the cookie for a fresh one.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "unknown_error";
    this.details = body.details ?? {};
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Internal: prevents a refresh loop when the refresh call itself 401s. */
  skipRefresh?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    return new ApiError(response.status, (await response.json()) as ApiErrorBody);
  } catch {
    return new ApiError(response.status, { message: response.statusText });
  }
}

/**
 * Refresh in flight, shared by every 401 that arrives while it runs.
 * Without this, a dashboard firing six queries at once would send six refresh
 * requests — and because refresh tokens rotate, five of them would present an
 * already-spent token and trip the server's reuse detection, logging the user
 * out for doing nothing wrong.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Registered by AuthProvider. A refresh the server refuses means the session
 * is over — expired, or revoked by a password change on another device — and
 * the UI must fall back to the login screen rather than keep rendering a page
 * where every request fails.
 */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        setAccessToken(null);
        onSessionLost?.();
        return false;
      }
      const data = (await response.json()) as { access_token: string };
      setAccessToken(data.access_token);
      return true;
    } catch {
      setAccessToken(null);
      onSessionLost?.();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function apiRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const { body, skipRefresh, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  if (body !== undefined) requestHeaders.set("Content-Type", "application/json");
  if (accessToken) requestHeaders.set("Authorization", `Bearer ${accessToken}`);

  const request: RequestInit = {
    ...rest,
    headers: requestHeaders,
    // Required for the refresh cookie to travel on same-origin API calls.
    credentials: "include",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  let response = await fetch(`${API_BASE_URL}${path}`, request);

  // A 401 usually just means the short-lived access token expired. Refresh once
  // and replay; if that fails, the session is genuinely over.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      requestHeaders.set("Authorization", `Bearer ${accessToken}`);
      response = await fetch(`${API_BASE_URL}${path}`, { ...request, headers: requestHeaders });
    }
  }

  if (!response.ok) throw await parseError(response);
  return response;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiRequest(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Called once on startup: exchanges the refresh cookie for an access token so a
 * reload does not look like a logout. Returns false when there is no session.
 */
export async function bootstrapSession(): Promise<boolean> {
  return refreshAccessToken();
}

export { API_BASE_URL };
